<template>
    <section class="mileage-admin">
        <div class="admin-panel">
            <div class="panel-heading"><h2>마일리지 상품 설정</h2><router-link to="/fit_diary_zone_five">참여 화면 ↗</router-link></div>
            <p class="admin-hint">저장한 상품명은 마일리지 이벤트 상품 안내에 표시됩니다. 금액도 상품명에 함께 입력할 수 있습니다.</p>
            <form @submit.prevent="savePrizes">
                <div v-for="(label, key) in labels" :key="key" class="schedule-field">
                    <label :for="'prize-' + key">{{ label }} 상품명</label>
                    <input :id="'prize-' + key" v-model="prizes[key]" type="text" maxlength="200" required :disabled="!prizesReady || saving">
                </div>
                <div class="calendar-form-actions"><button class="admin-primary" type="submit" :disabled="!prizesReady || saving">{{ saving ? '저장 중…' : '상품명 저장' }}</button></div>
            </form>
            <p class="save-message" role="status">{{ prizeMessage }}</p>
        </div>
        <div class="admin-panel reset-panel">
            <div class="panel-heading"><h2>마일리지 신청자 명단 초기화</h2><span>{{ names.length }}명</span></div>
            <p>현재 마일리지 신청자 전원과 각 신청자의 운동 기록을 삭제합니다. 삭제 후 복구할 수 없습니다. 상품 설정은 유지됩니다.</p>
            <p v-if="recordsReady">{{ names.length ? names.join(', ') : '등록된 신청자가 없습니다.' }}</p>
            <div class="schedule-field"><label for="mileage-reset">초기화하려면 ‘초기화’를 입력하세요.</label><input id="mileage-reset" v-model="resetText" autocomplete="off" :disabled="resetting"></div>
            <button type="button" :disabled="!recordsReady || !names.length || resetting || resetText !== '초기화'" @click="resetApplicants">{{ resetting ? '초기화 중…' : '신청자 명단 초기화' }}</button>
            <p class="save-message" role="status">{{ resetMessage }}</p>
        </div>
    </section>
</template>

<script>
import { getDatabase, ref, onValue, set, remove } from 'firebase/database';
import { MILEAGE_RECORDS_PATH, MILEAGE_PRIZES_PATH, defaultMileagePrizes, mileagePrizesFrom, mileagePrizeLabels } from '@/utils/mileage';

export default {
    data() {
        return { prizes: defaultMileagePrizes(), labels: mileagePrizeLabels, prizesReady: false, saving: false,
            prizeMessage: '상품 정보를 불러오는 중…', names: [], recordsReady: false,
            resetting: false, resetText: '', resetMessage: '신청자를 불러오는 중…', unsubscribers: [] };
    },
    mounted() {
        const db = getDatabase();
        this.unsubscribers.push(onValue(ref(db, MILEAGE_PRIZES_PATH), snapshot => {
            // Load once so live updates do not overwrite unsaved edits.
            if (!this.prizesReady) this.prizes = mileagePrizesFrom(snapshot.val());
            this.prizesReady = true;
            if (!this.saving) this.prizeMessage = '';
        }, () => { this.prizesReady = false; this.prizeMessage = '상품 정보를 불러오지 못했습니다. 연결 상태와 접근 권한을 확인해 주세요.'; }));
        this.unsubscribers.push(onValue(ref(db, MILEAGE_RECORDS_PATH), snapshot => {
            this.names = Object.keys(snapshot.val() || {});
            this.recordsReady = true;
            if (!this.resetting) this.resetMessage = '';
        }, () => { this.recordsReady = false; this.resetMessage = '신청자를 불러오지 못했습니다. 연결 상태와 접근 권한을 확인해 주세요.'; }));
    },
    beforeDestroy() { this.unsubscribers.forEach(unsubscribe => unsubscribe()); },
    methods: {
        async savePrizes() {
            if (!this.prizesReady || this.saving) return;
            const values = {};
            for (const key of Object.keys(this.labels)) {
                values[key] = this.prizes[key].trim();
                if (!values[key]) { this.prizeMessage = '모든 상품명을 입력해 주세요.'; return; }
            }
            this.saving = true;
            try {
                await set(ref(getDatabase(), MILEAGE_PRIZES_PATH), values);
                this.prizes = values;
                this.prizeMessage = '상품명을 저장했습니다.';
            } catch (error) { this.prizeMessage = '저장하지 못했습니다. 연결 상태와 접근 권한을 확인해 주세요.'; }
            finally { this.saving = false; }
        },
        async resetApplicants() {
            if (!this.recordsReady || !this.names.length || this.resetting || this.resetText !== '초기화') return;
            if (!confirm('마일리지 신청자 전원과 운동 기록을 삭제할까요? 이 작업은 복구할 수 없습니다.')) return;
            this.resetting = true;
            try {
                await remove(ref(getDatabase(), MILEAGE_RECORDS_PATH));
                this.resetText = '';
                this.resetMessage = '마일리지 신청자 명단과 운동 기록을 초기화했습니다.';
            } catch (error) { this.resetMessage = '초기화하지 못했습니다. 연결 상태와 접근 권한을 확인해 주세요.'; }
            finally { this.resetting = false; }
        }
    }
};
</script>

<style scoped>
.mileage-admin { min-width:0; }
.admin-panel { border:1px solid #e2e8f0; border-radius:16px; background:#fff; padding:18px; margin-bottom:18px; }
.panel-heading { display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:14px; }
.panel-heading h2 { font-size:17px; font-weight:800; line-height:1.5; margin:0; }
.panel-heading a { flex-shrink:0; font-size:14px; color:#2563eb; text-decoration:none; }
.panel-heading > span { flex-shrink:0; color:#475569; font-size:14px; }
.admin-hint { color:#475569; font-size:14px; line-height:1.7; margin:12px 0 0; }
.schedule-field { margin-top:16px; }
.schedule-field label { display:block; font-size:14px; color:#475569; font-weight:700; margin-bottom:7px; }
.schedule-field input { display:block; box-sizing:border-box; width:100%; min-height:44px; border:1px solid #dbe3ef; border-radius:10px; padding:10px 12px; background:#fff; color:#172033; font-size:15px; }
.schedule-field input:focus { outline:2px solid #bfdbfe; border-color:#60a5fa; }
.calendar-form-actions { display:flex; gap:8px; margin-top:16px; }
.admin-primary { border:0; border-radius:10px; background:#2563eb; color:#fff; min-height:44px; padding:10px 18px; font-size:14px; font-weight:700; }
.save-message { font-size:14px; line-height:1.7; color:#1d4ed8; margin:10px 0 0; }
.reset-panel { margin-top:28px; background:#fffafb; border-color:#ffe4e6; }
.reset-panel p { font-size:14px; color:#475569; line-height:1.7; margin-top:14px; overflow-wrap:anywhere; }
.reset-panel button { min-height:44px; margin-top:12px; border:1px solid #fecdd3; border-radius:8px; background:#fff; color:#be123c; padding:8px 12px; font-size:14px; }
button { cursor:pointer; }
button:disabled, input:disabled { opacity:.5; cursor:default; }
button:focus-visible, a:focus-visible { outline:3px solid #93c5fd; outline-offset:3px; }
@media (max-width:360px) { .panel-heading { flex-wrap:wrap; } }
</style>
